import { Stack } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UserRole } from '../../../constants/user-role';
import redux, { RootState } from '../../../redux';
import { Item } from '../../../redux/api/types';
import ItemIcon from './item-icon';
import ItemName from './item-name';

type NestedListProps = {
  customer?: Item | null;
  data: Item[];
  level?: number;
  setAnchorEl: (anchorEl: HTMLDivElement | null) => void;
};

const itemSx = {
  alignItems: 'center',
  display: 'flex',
  paddingBottom: 1,
  paddingLeft: 0.5,
  paddingTop: 1,
};

const NestedList = ({
  customer = null,
  data,
  level = 0,
  setAnchorEl,
}: NestedListProps): JSX.Element | null => {
  const context = useSelector((state: RootState) => state.api.context);
  const dispatch: typeof redux.store.dispatch = useDispatch();

  if (!context) {
    return null;
  }

  const handleClick = (item: Item) => {
    dispatch(
      redux.api.putContext({
        customer_group_uuid: customer ? item.uuid : null,
        customer_uuid: customer ? customer.uuid : item.uuid,
      }),
    );
    dispatch(redux.modal.close());
    setAnchorEl(null);
  };

  const onItemClick = (item: Item, level: number) => {
    if (
      [
        UserRole.CALIBRATOR,
        UserRole.MANAGER,
        UserRole.USER,
        UserRole.VIEWER,
      ].indexOf(context.role) < 0 ||
      level > 0
    ) {
      handleClick(item);
    }
  };

  return (
    <Stack direction="column" sx={{ paddingLeft: level > 0 ? 2 : 0 }}>
      {data.map((item, index) => (
        <Stack direction="column" key={index}>
          <Stack
            className="group-switcher-item"
            data-testid="group-switcher-item"
            direction="row"
            gap={0}
            onClick={() => onItemClick(item, level)}
            sx={{
              ...itemSx,
              cursor:
                [
                  UserRole.CALIBRATOR,
                  UserRole.MANAGER,
                  UserRole.USER,
                  UserRole.VIEWER,
                ].indexOf(context.role) < 0 || level > 0
                  ? 'pointer'
                  : 'initial',
            }}
          >
            <ItemIcon isCustomer={level === 0} />
            <ItemName isCustomer={level === 0} item={item} />
          </Stack>
          {item.customer_groups && item.customer_groups.length > 0 && (
            <NestedList
              customer={customer ?? item}
              data={item.customer_groups}
              level={level + 1}
              setAnchorEl={setAnchorEl}
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default NestedList;
